import React, { useState, useEffect, useRef } from 'react';
import { SearchState } from 'react-instantsearch-core';
import {
  debounce, isArray, isEqual, isString,
} from 'lodash';
import axios from 'axios';
import routes from '../../../utils/routes';
import PuppyCard from '../../body/card/puppy-card';
import { AdInterface, FacetObject } from '../admin-dashboard-tables/interfaces';

interface Props {
  count: number,
  title: string,
  listingsType: string,
  searchState: SearchState,
  selectedAgeValue: FacetObject[],
  searchIsHybrid: boolean,
  toggleSearchType: () => void,
  externalBreedRefinement?: string, // Breed name
  externalSellerRefinement?: number, // Seller ID
}

/**
 * Interface corresponding to SearchPreviewValidator in PHP
 */
interface SearchPreviewRequest {
  count: number,
  type: 'purebred' | 'hybrid',
  breeds?: string[], // Breed names
  seller?: number, // Seller ID
  sex?: ('male' | 'female')[],
  min_price?: number, // Units of cents
  max_price?: number, // Units of cents
  min_age?: number, // Units of months
  max_age?: number, // Units of months
}

/**
 * Algolia SearchState contains a price query formatted like the following examples:
 *  - "real_price <= 110000"
 *  - "real_price >= 105000"
 *  - "real_price:105000 TO 110000"
 *
 * This method takes that string as input and returns a [minPrice, maxPrice] array
 */
const extractMinAndMaxPrice = (
  priceQuery: string | undefined,
): [number | undefined, number | undefined] => {
  if (!priceQuery) {
    return [undefined, undefined];
  }

  if (priceQuery.includes(' TO ')) {
    const split = priceQuery.split(' TO ');
    return [
      parseInt(split[0].replace(/\D/g, ''), 10),
      parseInt(split[1].replace(/\D/g, ''), 10),
    ];
  }

  if (priceQuery.includes('>=') || priceQuery.includes('>')) {
    return [
      parseInt(priceQuery.replace(/\D/g, ''), 10),
      undefined,
    ];
  }

  if (priceQuery.includes('<=') || priceQuery.includes('<')) {
    return [
      undefined,
      parseInt(priceQuery.replace(/\D/g, ''), 10),
    ];
  }

  return [undefined, undefined];
};

/**
 * The Algolia SearchState numericFilters array contains many different filters. This method simply
 * extracts the filter that contains the string real_price, or undefined if it does not exist
 */
const getRealPriceFilter = (numericFilters: any): string | undefined => {
  if (!isArray(numericFilters)) {
    return undefined;
  }

  const realPriceFilters = numericFilters.filter((val) => isString(val) && val.includes('real_price'));

  return realPriceFilters.length
    ? realPriceFilters[0]
    : undefined;
};

/**
 * The sex field Algolia SearchState refinementList is typed as string[], however sometimes it's
 * not an array and is just a string. This reformats the field to the correct type to be used in
 * SearchPreviewRequest
 */
const formatRefinementListSex = (sex: string | string[] | undefined): ('male' | 'female')[] | undefined => {
  const arr = isArray(sex) ? sex : [sex];
  const filtered = arr.filter((val) => val === 'male' || val === 'female');

  return filtered.length
    ? filtered as ('male' | 'female')[]
    : undefined;
};

/**
 * The SearchResultsBlock component stores the value of the Age facet in it's own state, which is
 * passed in as selectedAgeValue. selectedAgeValue has different meanings depending on its value
 *
 * If selectedAgeValue > 1
 *  - Its units are in months
 *  - The search form is searching for listings less than selectedAgeValue months old
 *
 * If selectedAgeValue <= 1
 *  - Its units are in years
 *  - The search form is searching for listings older than selectedAgeValue years old
 *
 * This component takes the selectedAgeValue FacetObject and converts it to an array of the form
 * [minAge, maxAge]
 */
const extractMinAndMaxAge = (
  selectedAge: FacetObject,
): [number | undefined, number | undefined] => {
  if (!selectedAge) {
    return [undefined, undefined];
  }

  const num = parseInt(selectedAge.value, 10);

  return num > 1
    ? [undefined, num]
    : [num * 12, undefined];
};

/**
 * Takes the Algolia SearchState and the selectedAgeValue and maps them into the search state our
 * PHP logic expects, represented by SearchPreviewRequest (& the SearchPreviewValidator in PHP)
 */
const buildSearchPreviewRequest = (
  count: number,
  searchState: SearchState,
  selectedAgeValue: FacetObject[],
  searchIsHybrid: boolean,
  externalBreedRefinement?: string,
  externalSellerRefinement?: number,
): SearchPreviewRequest => {
  const realPriceFilter = getRealPriceFilter(searchState.configure?.numericFilters);
  const [minPrice, maxPrice] = extractMinAndMaxPrice(realPriceFilter);
  const [minAge, maxAge] = extractMinAndMaxAge(selectedAgeValue[0]);

  return {
    count,
    // We want to search the opposite of what the main search is doing
    type: searchIsHybrid ? 'purebred' : 'hybrid',
    // If the Breed refinement is defined externally (like on a Breed page), we simply use that.
    // Otherwise, we pull the breed refinement from search state
    breeds: externalBreedRefinement
      ? [externalBreedRefinement]
      : searchState.refinementList?.admin_breed_parents,
    seller: externalSellerRefinement,
    sex: formatRefinementListSex(searchState.refinementList?.sex),
    min_price: minPrice,
    max_price: maxPrice,
    min_age: minAge,
    max_age: maxAge,
  };
};

const PurebredHybridSearchCTA: React.FC<Props> = ({
    count,
    title,
    listingsType,
    searchState,
    selectedAgeValue,
    searchIsHybrid,
    toggleSearchType,
    externalBreedRefinement,
    externalSellerRefinement,
  }) => {
  const [previewListings, setPreviewListings] = useState<AdInterface[]>([]);
  const [searchPreviewRequest, setSearchPreviewRequest] = useState(
    buildSearchPreviewRequest(
      count,
      searchState,
      selectedAgeValue,
      searchIsHybrid,
      externalBreedRefinement,
      externalSellerRefinement,
    ),
  );

  const debouncedFetchPreviewListings = useRef(
    debounce((previewRequest: SearchPreviewRequest) => {
      axios.post(routes.apiSearchPreview(listingsType), previewRequest)
        .then(({ data }) => {
          setPreviewListings(data);
        })
        .catch((e) => {
          setPreviewListings([]);
        });
    }, 200),
  ).current;

  useEffect(() => {
    return () => {
      debouncedFetchPreviewListings.cancel();
    };
  }, [debouncedFetchPreviewListings]);

  // When search state is updated, fetch the new preview
  useEffect(() => {
    const newSearchPreviewRequest = buildSearchPreviewRequest(
      count,
      searchState,
      selectedAgeValue,
      searchIsHybrid,
      externalBreedRefinement,
      externalSellerRefinement,
    );

    // Only fetch if preview search state has changed. This avoids sending unnecessary requests
    // for irrelevant state changes such as sorts & text searches
    if (!isEqual(searchPreviewRequest, newSearchPreviewRequest)) {
      setSearchPreviewRequest(newSearchPreviewRequest);
      debouncedFetchPreviewListings(newSearchPreviewRequest);
    }
  }, [
    searchState,
    selectedAgeValue,
    searchIsHybrid,
    externalBreedRefinement,
    externalSellerRefinement,
    searchPreviewRequest,
  ]);

  // Run the preview on page load
  useEffect(() => {
    debouncedFetchPreviewListings(searchPreviewRequest);
  }, []);

  return (
    <div className="purebred-hybrid-cta-wrapper mt-4 mb-2">
      <button
        className="purebred-hybrid-cta-button bg-slate-blue rounded-md d-block text-left"
        onClick={toggleSearchType}
        type="button"
      >
        <section className="results-container">
          {previewListings.length > 0 && (
            <>
              <h2 className="text-center mb-4">
                {title}
              </h2>
              <div className="ais-InfiniteHits mb-4">
                <ul className="ais-InfiniteHits-list justify-content-center">
                  {previewListings.map((listing) => (
                    <li key={listing.id} className="ais-InfiniteHits-item">
                      <div className="react-puppy-card">
                        <PuppyCard
                          algpuppy={listing}
                          renderSimplePreview
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
          <div className="text-center">
            <div
              className="fake-cta-button btn btn-outline-primary rounded-pill text-uppercase py-2 px-5"
            >
              {title}
            </div>
          </div>
        </section>
      </button>
    </div>
  );
};

export default PurebredHybridSearchCTA;
